import React, { useCallback, useEffect, useRef, useState } from "react";

const BODY = /<body[^>]*>([\s\S]*)<\/body>/i;
const headOf = (html) => String(html || "").split(/<body[\s>]/i)[0];

const FIT_INSET = 8;

function parentHeightLimit(container) {
  const parent = container.parentElement;
  const max = parent
    ? parseFloat(window.getComputedStyle(parent).maxHeight)
    : NaN;
  return Number.isFinite(max) && max > 0 ? max : window.innerHeight * 0.75;
}

/**
 * Renders a filled HTML certificate in a sandboxed iframe (no scripts),
 * scaled to fit its container. Same-origin access lets the parent apply
 * shrink-to-fit on long values and report overflowing text boxes.
 */
export default function HtmlCertificateFrame({
  html,
  width,
  height,
  title = "Certificate preview",
  onOverflow,
  className,
  maxHeight,
  fitParentHeight = false,
}) {
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const loadedRef = useRef(false);
  const [scale, setScale] = useState(1);
  const [srcDoc, setSrcDoc] = useState(html);
  const srcDocRef = useRef(html);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const update = () => {
      const inset = fitParentHeight ? FIT_INSET : 0;
      const byWidth = Math.max(0, container.clientWidth - inset) / width;
      const limit = Math.min(
        maxHeight || Infinity,
        fitParentHeight ? parentHeightLimit(container) - inset : Infinity,
      );
      const byHeight = Number.isFinite(limit) ? limit / height : Infinity;
      setScale(Math.max(0.05, Math.min(byWidth, byHeight)));
    };
    update();
    const frame = window.requestAnimationFrame(update);
    const observer = new ResizeObserver(update);
    observer.observe(container);
    if (fitParentHeight) window.addEventListener("resize", update);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      if (fitParentHeight) window.removeEventListener("resize", update);
    };
  }, [width, height, maxHeight, fitParentHeight]);

  const measure = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    const run = () => {
      doc.querySelectorAll('[data-autofit="shrink"]').forEach((node) => {
        const min = Number(node.getAttribute("data-min-font-size")) || 8;
        let size = parseFloat(node.style.fontSize) || 16;
        let guard = 0;
        while (
          (node.scrollHeight > node.clientHeight + 1 ||
            node.scrollWidth > node.clientWidth + 1) &&
          size > min &&
          guard < 60
        ) {
          size = Math.max(min, size - 0.5);
          // eslint-disable-next-line no-param-reassign
          node.style.fontSize = `${size}px`;
          guard += 1;
        }
      });
      if (onOverflow) {
        const overflowing = [...doc.querySelectorAll(".cf-text")]
          .filter(
            (node) =>
              node.scrollHeight > node.clientHeight + 1 ||
              node.scrollWidth > node.clientWidth + 1,
          )
          .map((node) => node.getAttribute("data-id"));
        onOverflow(overflowing);
      }
    };
    if (doc.fonts?.ready) doc.fonts.ready.then(run);
    else run();
  }, [onOverflow]);

  useEffect(() => {
    if (html === srcDocRef.current) return;
    const doc = frameRef.current?.contentDocument;
    const body = BODY.exec(html || "");
    if (
      loadedRef.current &&
      doc?.body &&
      body &&
      headOf(html) === headOf(srcDocRef.current)
    ) {
      doc.body.innerHTML = body[1];
      srcDocRef.current = html;
      measure();
      return;
    }
    loadedRef.current = false;
    srcDocRef.current = html;
    setSrcDoc(html);
  }, [html, measure]);

  const handleLoad = useCallback(() => {
    loadedRef.current = true;
    measure();
  }, [measure]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: "100%",
        padding: fitParentHeight ? FIT_INSET / 2 : undefined,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: width * scale,
          height: height * scale,
          margin: "0 auto",
        }}
        className="relative overflow-hidden rounded-sm shadow-lg ring-1 ring-cfCardBorder bg-cfSecondary"
      >
        <iframe
          ref={frameRef}
          title={title}
          sandbox="allow-same-origin"
          srcDoc={srcDoc}
          onLoad={handleLoad}
          style={{
            width,
            height,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        />
      </div>
    </div>
  );
}
